"use client";

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Trash2, Plus, Minus, ShoppingBag, TicketPercent, Loader2, XCircle, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { CartSkeleton } from "@/components/skeletons";
import { useCart } from "@/context/CartContext";
import {
  calculateTax,
  calculateTotal,
  isTaxFree,
} from "@/constants/pricing";
import { usePricingSettings } from "@/hooks/usePricingSettings";
import { useValidateCouponMutation } from "@/store";
import { toast } from "sonner";

export default function CartScreen() {
  const navigate = useNavigate();
  const { cartItems, updateQuantity, removeFromCart, getTotalPrice, appliedCoupon, applyCoupon } =
    useCart();
  const { taxRate } = usePricingSettings();
  const [isLoading, setIsLoading] = useState(true);
  const [couponCode, setCouponCode] = useState("");
  
  const [validateCoupon, { isLoading: isValidating }] = useValidateCouponMutation();

  useEffect(() => {
    // Simulate loading cart items
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  const handleQuantityChange = (id: string | number, change: number) => {
    const item = cartItems.find((i) => i.id === id);
    if (item) {
      updateQuantity(id, Math.max(1, item.quantity + change));
      // Re-validate coupon if quantities change? 
      // For now, simpler: if item count changes, clear coupon to be safe or re-validate on checkout.
      if (appliedCoupon) {
        toast.info("Item quantities changed. Please re-apply coupon if needed.");
        applyCoupon(null);
      }
    }
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error("Please enter a coupon code");
      return;
    }

    try {
      const subtotal = getTotalPrice();
      // Prep items for validation
      const validationItems = cartItems.map(item => ({
        productId: String(item.productId || item.id),
        quantity: item.quantity,
        subtotal: item.price * item.quantity
      }));

      const result = await validateCoupon({
        code: couponCode.trim(),
        items: validationItems,
        subtotal
      }).unwrap();

      if (result.success && result.data) {
        applyCoupon({
          code: couponCode.trim(),
          discountAmount: result.data.discountAmount,
          isFreeDelivery: result.data.isFreeDelivery,
          coupon: result.data.coupon
        });
        toast.success(`Coupon "${couponCode}" applied successfully!`);
        setCouponCode("");
      } else {
        toast.error("Failed to apply coupon");
      }
    } catch (error: any) {
      const message = error?.data?.error || "Invalid coupon code";
      toast.error(message);
    }
  };

  const handleRemoveCoupon = () => {
    applyCoupon(null);
    toast.info("Coupon removed");
  };

  const subtotal = getTotalPrice();
  const effectiveDeliveryFee = 0; // Disabled: calculated by dispatch
  const discount = appliedCoupon?.discountAmount || 0;
  
  const tax = calculateTax(subtotal, taxRate);
  const total = calculateTotal(subtotal, taxRate, effectiveDeliveryFee) - discount;

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <h1 className="text-4xl font-bold mb-2">Shopping Cart</h1>
          <p className="text-muted-foreground">Loading your cart...</p>
        </motion.div>
        <CartSkeleton itemCount={3} />
      </div>
    );
  }

  if (cartItems.length === 0) {
    return (
      <div className="container mx-auto px-4 py-16">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          <ShoppingBag className="h-24 w-24 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-3xl font-bold mb-2">Your cart is empty</h2>
          <p className="text-muted-foreground mb-6">
            Start adding items to your cart!
          </p>
          <Button onClick={() => navigate("/our-menu")}>Browse Products</Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="mb-8"
      >
        <h1 className="text-4xl font-bold mb-2">Shopping Cart</h1>
        <p className="text-muted-foreground">
          {cartItems.length} item(s) in your cart
        </p>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Cart Items */}
        <div className="lg:col-span-2 space-y-4">
          {cartItems.map((item, index) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
            >
              <Card>
                <CardContent className="p-4">
                  <div className="flex gap-4">
                    <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-lg overflow-hidden bg-card shrink-0">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0 flex flex-col justify-between">
                      <div className="min-w-0">
                        <h3 className="font-semibold text-base sm:text-lg truncate">
                          {item.name}
                        </h3>
                        {item.variations &&
                          Object.keys(item.variations).length > 0 && (
                            <div className="mt-1 space-y-0.5">
                              {Object.entries(item.variations).map(
                                ([variationName, optionName]) => (
                                  <p
                                    key={variationName}
                                    className="text-xs sm:text-sm text-muted-foreground truncate"
                                  >
                                    <span className="font-medium">
                                      {variationName}:
                                    </span>{" "}
                                    {optionName}
                                  </p>
                                ),
                              )}
                            </div>
                          )}
                        <p className="text-sm text-muted-foreground mt-1">
                          ₦{item.price.toLocaleString()} each
                        </p>
                      </div>
                      <div className="flex items-center justify-between gap-2 mt-4">
                        <div className="flex items-center gap-1">
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleQuantityChange(item.id, -1)}
                          >
                            <Minus className="h-4 w-4" />
                          </Button>
                          <span className="w-8 text-center font-medium text-sm">
                            {item.quantity}
                          </span>
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleQuantityChange(item.id, 1)}
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                        <div className="flex items-center gap-1 sm:gap-2">
                          <span className="text-sm sm:text-base font-bold whitespace-nowrap">
                            ₦{(item.price * item.quantity).toLocaleString()}
                          </span>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeFromCart(item.id)}
                            className="text-destructive hover:text-destructive h-8 w-8"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Order Summary */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="lg:col-span-1 space-y-6"
        >
          {/* Coupon Code Section */}
          <Card className="border-primary/20 shadow-sm overflow-hidden">
            <CardHeader className="pb-3 bg-primary/5">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <TicketPercent className="h-4 w-4 text-primary" />
                Promotion Code
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <AnimatePresence mode="wait">
                {!appliedCoupon ? (
                  <motion.div 
                    key="apply"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="flex gap-2"
                  >
                    <Input 
                      placeholder="ENTER CODE" 
                      className="font-mono uppercase tracking-wider text-xs"
                      value={couponCode} 
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      onKeyDown={(e) => e.key === "Enter" && handleApplyCoupon()}
                    />
                    <Button 
                      onClick={handleApplyCoupon} 
                      variant="default" 
                      className="h-auto py-2"
                      disabled={isValidating || !couponCode.trim()}
                    >
                      {isValidating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply"}
                    </Button>
                  </motion.div>
                ) : (
                  <motion.div 
                    key="applied"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="space-y-3"
                  >
                    <div className="flex items-center justify-between p-3 rounded-lg bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800">
                      <div className="flex items-center gap-2">
                        <div className="bg-green-100 dark:bg-green-800/20 p-1.5 rounded-full">
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                        </div>
                        <div>
                          <p className="text-xs font-bold text-green-700 dark:text-green-400 font-mono tracking-tighter">
                            {appliedCoupon.code}
                          </p>
                          <p className="text-[10px] text-green-600/70">Applied successfully</p>
                        </div>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={handleRemoveCoupon}
                        className="h-7 w-7 text-green-700 hover:bg-green-100 dark:hover:bg-green-800/30"
                      >
                        <XCircle className="h-4 w-4" />
                      </Button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </CardContent>
          </Card>

          <Card className="sticky top-24">
            <CardHeader>
              <CardTitle>Order Summary</CardTitle>
              <CardDescription>Review your order details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span>₦{subtotal.toLocaleString()}</span>
                </div>
                
                {discount > 0 && (
                  <div className="flex justify-between text-sm">
                    <div className="flex items-center gap-1.5 text-green-600 font-medium">
                      <TicketPercent className="h-3.5 w-3.5" />
                      <span>Discount ({appliedCoupon?.code})</span>
                    </div>
                    <span className="text-green-600 font-semibold">-₦{discount.toLocaleString()}</span>
                  </div>
                )}

                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Tax</span>
                  <span
                    className={
                      isTaxFree(taxRate) ? "text-green-600 font-medium" : ""
                    }
                  >
                    {isTaxFree(taxRate) ? "Free" : `₦${tax.toLocaleString()}`}
                  </span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-muted-foreground">Delivery Fee</span>
                  {appliedCoupon?.isFreeDelivery ? (
                    <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-[10px] font-bold uppercase tracking-tight">
                      <CheckCircle2 className="h-3 w-3" />
                      Free Delivery
                    </div>
                  ) : (
                    <span className="text-xs italic text-muted-foreground">calculated to dispatch not us</span>
                  )}
                </div>
                
                <div className="border-t pt-2 flex justify-between font-bold text-lg">
                  <span>Total</span>
                  <AnimatePresence mode="wait">
                    <motion.span 
                      key={total}
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-primary"
                    >
                      ₦{total.toLocaleString()}
                    </motion.span>
                  </AnimatePresence>
                </div>
              </div>
              
              {appliedCoupon?.isFreeDelivery && (
                <div className="p-3 rounded-lg bg-green-50 border border-green-100 flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5" />
                  <p className="text-xs text-green-700">
                    Your coupon includes <span className="font-bold underline">Free Delivery</span>!
                  </p>
                </div>
              )}
            </CardContent>
            <CardFooter className="flex flex-col gap-3">
              <Button
                className="w-full text-base font-bold shadow-lg shadow-primary/20"
                size="lg"
                onClick={() => navigate("/checkout")}
              >
                Proceed to Checkout
              </Button>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => navigate("/our-menu")}
              >
                Continue Shopping
              </Button>
            </CardFooter>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}

